import React from 'react';
import { PromoCode } from '@/hooks/sms/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { MoreHorizontal, Edit, Copy, Trash2, Tag, Percent, DollarSign, Gift } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface PromoCodeTableProps {
  promoCodes: PromoCode[];
  onEdit: (id: number) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
}

const PromoCodeTable: React.FC<PromoCodeTableProps> = ({
  promoCodes,
  onEdit,
  onDuplicate,
  onDelete
}) => {
  const formatDiscount = (code: PromoCode) => {
    switch (code.discount_type) {
      case 'percentage_off':
        return `${code.discount_value}% off`;
      case 'dollar_off':
        return `$${code.discount_value} off`;
      case 'free_item':
        return `Free item (Cost: $${code.item_cost || 0})`;
      default:
        return 'Unknown';
    }
  };

  const getDiscountIcon = (type: string) => {
    switch (type) {
      case 'percentage_off':
        return <Percent className="h-4 w-4" />;
      case 'dollar_off':
        return <DollarSign className="h-4 w-4" />;
      case 'free_item':
        return <Gift className="h-4 w-4" />;
      default:
        return <Tag className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (code: PromoCode) => {
    if (!code.active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    
    if (code.end_date && new Date(code.end_date) < new Date()) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    
    if (code.usage_limit && code.current_usage >= code.usage_limit) {
      return <Badge variant="destructive">Limit Reached</Badge>;
    }
    
    if (code.start_date && new Date(code.start_date) > new Date()) {
      return <Badge variant="outline">Scheduled</Badge>;
    }
    
    return <Badge variant="default">Active</Badge>;
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Code</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead>Usage</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Created</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {promoCodes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                No promo codes found
              </TableCell>
            </TableRow>
          ) : (
            promoCodes.map((code) => (
              <TableRow key={code.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {getDiscountIcon(code.discount_type)}
                    <span className="font-mono font-medium">{code.code}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{code.name}</p>
                    {code.min_order_value && (
                      <p className="text-xs text-muted-foreground">
                        Min order: ${code.min_order_value}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div>
                    <p>{formatDiscount(code)}</p>
                    {code.max_discount && code.discount_type === 'percentage_off' && (
                      <p className="text-xs text-muted-foreground">
                        Max: ${code.max_discount}
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p>{code.current_usage} used</p>
                    {code.usage_limit && (
                      <p className="text-xs text-muted-foreground">
                        of {code.usage_limit} limit
                      </p>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(code)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(code.created_at), { addSuffix: true })}
                </TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onEdit(code.id)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onDuplicate(code.id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => onDelete(code.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default PromoCodeTable;